/**
 * 上传新文档 / 编辑文档弹窗（旧 DocumentManagementView.vue 中的弹窗）。
 * 正文以 .md 文件上传；编辑时不选文件则保留原内容，slug、语言、分类不可改。
 */
import { useState } from 'react'
import { App, Button, Divider, Form, Input, Modal, Select, Switch, Typography, Upload, type UploadFile } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import type { MarkdownDocument } from '@/api/services/documents'
import { CATEGORY_OPTIONS, LANGUAGE_OPTIONS, MAX_MARKDOWN_BYTES } from './constants'
import { useSaveDocument } from './hooks'

interface DocumentFormValues {
  slug: string
  language: string
  category: string
  title: string
  description: string
  seo_title: string
  seo_description: string
  seo_keywords: string
  is_published: boolean
}

function initialValues(editingDoc: MarkdownDocument | null): DocumentFormValues {
  return {
    slug: editingDoc?.slug ?? '',
    language: editingDoc?.language ?? 'zh',
    category: editingDoc?.category ?? 'docs',
    title: editingDoc?.title ?? '',
    description: editingDoc?.description ?? '',
    seo_title: editingDoc?.seo_title ?? '',
    seo_description: editingDoc?.seo_description ?? '',
    seo_keywords: editingDoc?.seo_keywords ?? '',
    is_published: editingDoc?.is_published ?? false
  }
}

interface DocumentFormProps {
  editingDoc: MarkdownDocument | null
  onClose: () => void
}

function DocumentForm({ editingDoc, onClose }: Readonly<DocumentFormProps>) {
  const { message } = App.useApp()
  const [form] = Form.useForm<DocumentFormValues>()
  const saveDocument = useSaveDocument()
  const [markdownFile, setMarkdownFile] = useState<File | null>(null)
  const isEdit = editingDoc !== null
  const slug = Form.useWatch('slug', form)
  const language = Form.useWatch('language', form)
  const isPublished = Form.useWatch('is_published', form)

  const fileList: UploadFile[] = markdownFile ? [{ uid: '-1', name: markdownFile.name, status: 'done' }] : []

  // 只校验不上传，文件随表单一起提交
  const beforeUpload = (file: File) => {
    if (!file.name.endsWith('.md')) {
      message.error('只支持 .md 文件')
      return false
    }
    if (file.size > MAX_MARKDOWN_BYTES) {
      message.error('文件大小不能超过 5MB')
      return false
    }
    setMarkdownFile(file)
    return false
  }

  const handleSubmit = async () => {
    const values = await form.validateFields().catch(() => null)
    if (!values) return
    if (!isEdit && !markdownFile) {
      message.error('请上传 Markdown 文件')
      return
    }

    // 可选字段留空时不提交（与旧版一致）
    const optional = {
      description: values.description || undefined,
      seo_title: values.seo_title || undefined,
      seo_description: values.seo_description || undefined,
      seo_keywords: values.seo_keywords || undefined
    }
    try {
      if (isEdit) {
        await saveDocument.mutateAsync({
          id: editingDoc.id,
          data: {
            title: values.title,
            ...optional,
            is_published: values.is_published,
            markdown_file: markdownFile ?? undefined
          }
        })
        message.success('文档更新成功')
      } else {
        await saveDocument.mutateAsync({
          data: {
            slug: values.slug,
            language: values.language,
            category: values.category,
            title: values.title,
            ...optional,
            is_published: values.is_published,
            markdown_file: markdownFile as File
          }
        })
        message.success('文档创建成功')
      }
      onClose()
    } catch (error: any) {
      // 失败时弹窗保持打开，已填内容不丢
      console.error('提交失败:', error)
      message.error(error.response?.data?.detail || '操作失败')
    }
  }

  return (
    <Modal
      open
      title={isEdit ? '编辑文档' : '上传新文档'}
      width={800}
      confirmLoading={saveDocument.isPending}
      onOk={() => void handleSubmit()}
      onCancel={onClose}
    >
      <Form<DocumentFormValues>
        form={form}
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 18 }}
        initialValues={initialValues(editingDoc)}
      >
        <Form.Item
          label="URL标识 (slug)"
          name="slug"
          rules={[{ required: true, message: '请输入 slug' }]}
          extra={`前端访问地址: /${language || 'zh'}/docs/${slug || 'slug'}`}
        >
          <Input placeholder="如: privacy (只允许小写字母、数字、连字符)" disabled={isEdit} />
        </Form.Item>
        <Form.Item label="语言" name="language" rules={[{ required: true, message: '请选择语言' }]}>
          <Select aria-label="语言" options={LANGUAGE_OPTIONS} disabled={isEdit} />
        </Form.Item>
        <Form.Item label="分类" name="category" rules={[{ required: true, message: '请选择分类' }]}>
          <Select aria-label="分类" options={CATEGORY_OPTIONS} disabled={isEdit} />
        </Form.Item>
        <Form.Item label="页面标题" name="title" rules={[{ required: true, message: '请输入页面标题' }]}>
          <Input placeholder="如: 隐私政策" />
        </Form.Item>
        <Form.Item label="简短描述" name="description">
          <Input.TextArea rows={2} placeholder="用于列表显示的简短描述" />
        </Form.Item>

        <Divider titlePlacement="start">SEO 设置（可选）</Divider>
        <Form.Item label="SEO 标题" name="seo_title">
          <Input placeholder="留空则使用页面标题" />
        </Form.Item>
        <Form.Item label="SEO 描述" name="seo_description">
          <Input.TextArea rows={2} placeholder="搜索引擎显示的描述" />
        </Form.Item>
        <Form.Item label="SEO 关键词" name="seo_keywords">
          <Input placeholder="多个关键词用逗号分隔" />
        </Form.Item>

        <Divider titlePlacement="start">文件上传</Divider>
        <Form.Item label="Markdown 文件" required={!isEdit} extra="仅支持 .md 文件，最大 5MB">
          <Upload
            accept=".md"
            maxCount={1}
            fileList={fileList}
            beforeUpload={beforeUpload}
            onRemove={() => setMarkdownFile(null)}
          >
            <Button icon={<UploadOutlined />}>选择 .md 文件</Button>
          </Upload>
        </Form.Item>

        <Form.Item label="是否发布">
          <Form.Item name="is_published" valuePropName="checked" noStyle>
            <Switch aria-label="是否发布" />
          </Form.Item>
          <Typography.Text style={{ marginLeft: 8 }}>{isPublished ? '立即发布' : '保存为草稿'}</Typography.Text>
        </Form.Item>
      </Form>
    </Modal>
  )
}

interface DocumentFormModalProps {
  open: boolean
  // 为 null 时新建，否则编辑该文档
  editingDoc: MarkdownDocument | null
  onClose: () => void
}

// 每次打开都重新挂载表单，关闭后状态不残留
export function DocumentFormModal({ open, editingDoc, onClose }: Readonly<DocumentFormModalProps>) {
  if (!open) return null
  return <DocumentForm key={editingDoc?.id ?? 'new'} editingDoc={editingDoc} onClose={onClose} />
}
